import { useMemo, useCallback } from "react";
import { useStore } from "hooks/use-store";
import { useTranslation } from "react-i18next";
import { PLACEHOLDER } from "utils/constants";
import { Color } from "utils/theme";
import { EcoRange } from "types/eco-sog";
import { RecommendedSogType } from "types/enums";

const MIN_SAIL_SOG = 3.0; // Minimum SOG to consider trip started
const VALID_SOG_THRES = 1.5; // Minimum SOG to use to calculate fuel rate
const ECO_SPEED_TOLERANCE = 2.0; // Tolerance for being considered in eco speed

export const fuelViewColors = {
  FuelRateNmi: Color.Orange[400],
  FuelRateH: Color.Orange[400],
  SOG: Color.Blue[400],
  EcoSOG: Color.Emerald[400],
  FuelCurve: Color.Blue[300],
  Bad: Color.Rose[600],
  Good: Color.Emerald[400],
};

export const useFuelStatistics = () => {
  const { t } = useTranslation("translation", { keyPrefix: "fuel_curve" });

  const config = useStore((state) => state.config);
  const sog = useStore((state) => state.sog);
  const rpm = useStore((state) => state.rpm);
  const safetySpeed = useStore((state) => state.rog);
  const ecoSogData = useStore((state) => state.ecoSog);

  // Average fuel rate (l/nm) based on current fuel rate
  const avgFuelRateNmi = useMemo(() => {
    // Use running average if available
    if (ecoSogData?.avg_fuel_rate_l_nm?.["30s"]) {
      const avrFuelRateNmi = ecoSogData.avg_fuel_rate_l_nm["30s"];
      if (avrFuelRateNmi && avrFuelRateNmi < 1000) {
        // Sanity check
        return avrFuelRateNmi;
      }
    }

    return null;
  }, [ecoSogData]);

  // Average fuel rate (l/h) based on current fuel rate and SOG
  const avgFuelRateH = useMemo(() => {
    // Use running average if available
    if (ecoSogData?.avg_fuel_rate_l_h?.["10s"]) {
      const avrFuelRateH = ecoSogData.avg_fuel_rate_l_h["10s"];
      if (avrFuelRateH && avrFuelRateH < 1000) {
        // Sanity check
        return avrFuelRateH;
      }
    }

    return null;
  }, [ecoSogData, sog]);

  // Has trip started?
  const hasTripStarted = useMemo(() => {
    if (sog !== null && sog >= MIN_SAIL_SOG) {
      return true;
    }
    if (!ecoSogData?.max_sog || ecoSogData?.max_sog > MIN_SAIL_SOG) {
      return true;
    }
    return false;
  }, [ecoSogData?.max_sog, sog]);

  // Fuel savings compared to target fuel rate
  const fuelSavings = useMemo(() => {
    if (ecoSogData?.fuel_deviation_from_target) {
      return -ecoSogData.fuel_deviation_from_target;
    }
    if (ecoSogData?.fuel_deviation_from_pred?.trip) {
      return -ecoSogData.fuel_deviation_from_pred.trip;
    }
    return 0;
  }, [ecoSogData]);

  // Current eco speed recommendation
  const ecoSogSpeed =
    ecoSogData?.eco_speed && ecoSogData?.eco_speed > 0
      ? ecoSogData?.eco_speed
      : null;

  // Current eco rpm recommendation
  const ecoSogRpm =
    ecoSogData?.eco_rpm && ecoSogData?.eco_rpm > 0 ? ecoSogData?.eco_rpm : null;

  // Maximum fuel rate in fuel curve
  const maxFuelRateInCurve =
    ecoSogData?.fuel_curve_stats?.max_fuel_rate_per_nm || 0;

  // Minimum fuel rate in fuel curve
  const minFuelRateInCurve =
    ecoSogData?.fuel_curve_stats?.min_fuel_rate_per_nm || 0;

  // Warning threshold for fuel rate (l/nm), this will always be equal or higher than the target fuel rate
  const fuelRateWarnThreshold =
    ecoSogData?.fuel_rate_warn_threshold || config?.target_fuel_rate;

  // Do we have either eco speed or safety speed recommendation?
  const hasRecommendedSog = useMemo(
    () =>
      (ecoSogSpeed && ecoSogSpeed > 0) ||
      (safetySpeed !== null && safetySpeed > 0),
    [ecoSogSpeed, safetySpeed]
  );

  // Is current SOG above safety speed?
  // A safety speed of 0 means no safety limit is set, so never unsafe
  const isSogUnsafe = useMemo(
    () =>
      sog !== null && safetySpeed !== null && safetySpeed > 0
        ? sog > safetySpeed
        : false,
    [safetySpeed, sog]
  );

  // Which SOG recommendation has priority now (eco or safety)?
  // If both are avilable and we are above safety speed, we should prioritize safety speed.
  const recommendedSogType = useMemo((): RecommendedSogType | null => {
    if (!hasRecommendedSog) {
      return null;
    }
    if (ecoSogSpeed && safetySpeed) {
      if (isSogUnsafe) {
        return RecommendedSogType.Safety;
      }
      return RecommendedSogType.Eco;
    }
    if (ecoSogSpeed) {
      return RecommendedSogType.Eco;
    } else {
      return RecommendedSogType.Safety;
    }
  }, [hasRecommendedSog, ecoSogSpeed, safetySpeed, isSogUnsafe]);

  // Value of current recommended SOG (safety or eco)
  const recommendedSog =
    recommendedSogType === RecommendedSogType.Eco
      ? ecoSogSpeed?.toFixed(0)
      : safetySpeed?.toFixed(0) || PLACEHOLDER;

  const isInEcoSpeed = useMemo(() => {
    // Is SOG close to eco speed?
    return (
      ecoSogSpeed &&
      sog !== null &&
      Math.abs(sog - ecoSogSpeed) < ECO_SPEED_TOLERANCE
    );
  }, [sog, ecoSogSpeed]);

  // Is SOG close to eco speed and below safety speed?
  const isInEcoSpeedAndSafe = isInEcoSpeed && !isSogUnsafe;

  // Check if a value is within any of the eco ranges for SOG
  const isValueInEcoRange = useCallback(
    (value: number) => {
      const ecoRanges: EcoRange[] = ecoSogData?.eco_ranges || [];
      let inRange = false;
      ecoRanges.map((range) => {
        if (
          value >= Math.floor(range?.start) &&
          value <= Math.ceil(range?.end)
        ) {
          inRange = true;
          return;
        }
      });

      return inRange;
    },
    [ecoSogData]
  );

  // Check if a value is within any of the eco ranges for RPM
  const isValueInEcoRangeRpm = useCallback(
    (value: number) => {
      const ecoRanges: EcoRange[] = ecoSogData?.eco_ranges || [];
      let inRange = false;
      ecoRanges.map((range) => {
        if (
          value >= Math.floor(range?.start_rpm) &&
          value <= Math.ceil(range?.end_rpm)
        ) {
          inRange = true;
          return;
        }
      });

      return inRange;
    },
    [ecoSogData]
  );

  // Is SOG within any of the eco ranges?
  const isInEcoRange = useMemo(() => {
    return (
      sog !== null &&
      (isValueInEcoRange(sog) ||
        (ecoSogSpeed && Math.round(ecoSogSpeed) === Math.round(sog)))
    );
  }, [sog, ecoSogSpeed, isValueInEcoRange]);

  // Is RPM within any of the eco ranges?
  const isInEcoRangeRpm = useMemo(() => {
    return (
      rpm !== null &&
      (isValueInEcoRangeRpm(rpm) ||
        (ecoSogRpm && Math.round(ecoSogRpm / 100) === Math.round(rpm / 100)))
    );
  }, [rpm, ecoSogRpm, isValueInEcoRangeRpm]);

  // Is fuel rate above the warning threshold?
  const isFuelRateBad =
    avgFuelRateNmi &&
    fuelRateWarnThreshold &&
    avgFuelRateNmi > fuelRateWarnThreshold &&
    sog !== null &&
    sog > VALID_SOG_THRES;

  // Color for current SOG value
  const currentSogColor = isSogUnsafe
    ? Color.Rose[600]
    : isInEcoRange && !isFuelRateBad
    ? Color.Emerald[400]
    : Color.Blue[400];

  // Is trip average fuel rate above the target fuel rate?
  const isTripFuelRateBad = useMemo(() => {
    if (!hasTripStarted) {
      return false;
    }
    if (ecoSogData?.avg_fuel_rate_l_nm?.sailing && config?.target_fuel_rate) {
      // Trip average fuel rate is bad if it's above the target fuel rate
      return ecoSogData?.avg_fuel_rate_l_nm?.sailing > config.target_fuel_rate;
    }
    return false;
  }, [ecoSogData, config, fuelRateWarnThreshold, hasTripStarted]);

  // Fuel curve status message
  const fuelCurveStatusMessage = useMemo(() => {
    if (!ecoSogData) {
      return t("fuel_curve_status.waiting_for_model");
    }
    if (!ecoSogData?.fuel_curve_stats?.state) {
      return t("fuel_curve_status.model_unavailable");
    }
    if (ecoSogData?.fuel_curve_stats?.state === "no_fuel_rate_data") {
      return t("fuel_curve_status.no_fuel_rate_data");
    }
    if (ecoSogData?.fuel_curve_stats?.state === "not_enough_data") {
      return t("fuel_curve_status.not_enough_data");
    }
    if (ecoSogData?.fuel_curve_stats?.state === "not_calculated") {
      return t("fuel_curve_status.not_calculated");
    }
    return null;
  }, [ecoSogData]);

  // Fuel status message
  const fuelStatusMessage = useMemo(() => {
    if (isSogUnsafe) {
      return config?.company_settings?.has_comfort_speed
        ? t("comfort_speed_exceeded")
        : t("safety_speed_exceeded");
    }
    if (isInEcoRange) {
      return t("optimal_speed");
    }
    return fuelCurveStatusMessage;
  }, [isSogUnsafe, isInEcoRange, fuelCurveStatusMessage, config]);

  // Check that we have fuel curve data, and that there is at least some fuel rate data available
  const hasFuelRateData =
    ecoSogData?.fuel_curve_stats?.state &&
    ecoSogData?.fuel_curve_stats?.state !== "no_fuel_rate_data";

  const canUseEcoMode =
    hasFuelRateData || ecoSogData?.eco_ranges || ecoSogData?.eco_speed;

  const safetySpeedLabel = config?.company_settings?.has_comfort_speed
    ? t("comfort_speed")
    : t("safety_speed");

  const safetySpeedExceededLabel = config?.company_settings?.has_comfort_speed
    ? t("comfort_speed_exceeded")
    : t("safety_speed_exceeded");

  const safetyLimitLabel = config?.company_settings?.has_comfort_speed
    ? t("comfort_limit")
    : t("safety_limit");

  const displayFuelRateNmi =
    avgFuelRateNmi && sog !== null && sog > VALID_SOG_THRES
      ? avgFuelRateNmi.toFixed(1)
      : PLACEHOLDER;

  const displayFuelRateH = avgFuelRateH
    ? Math.round(avgFuelRateH)
    : PLACEHOLDER;

  const displayTripAvgSpeed =
    ecoSogData?.avg_sog?.sailing?.toFixed(0) || PLACEHOLDER;

  const displayTripFuelRateNmi = hasTripStarted
    ? ecoSogData?.avg_fuel_rate_l_nm?.sailing?.toFixed(1) || PLACEHOLDER
    : PLACEHOLDER;

  const displayFuelSavings = fuelSavings
    ? (fuelSavings >= 0 ? "+" : "") + fuelSavings.toFixed(1)
    : PLACEHOLDER;

  return {
    MIN_SAIL_SOG,
    avgFuelRateNmi,
    avgFuelRateH,
    hasTripStarted,
    fuelSavings,
    displayFuelRateNmi,
    displayFuelRateH,
    displayTripAvgSpeed,
    displayTripFuelRateNmi,
    displayFuelSavings,
    ecoSogSpeed,
    ecoSogRpm,
    maxFuelRateInCurve,
    minFuelRateInCurve,
    fuelRateWarnThreshold,
    hasRecommendedSog,
    recommendedSogType,
    recommendedSog,
    isSogUnsafe,
    isInEcoSpeed,
    isInEcoSpeedAndSafe,
    isValueInEcoRange,
    isValueInEcoRangeRpm,
    isInEcoRange,
    isInEcoRangeRpm,
    currentSogColor,
    isFuelRateBad,
    isTripFuelRateBad,
    fuelCurveStatusMessage,
    fuelStatusMessage,
    hasFuelRateData,
    canUseEcoMode,
    safetySpeedLabel,
    safetySpeedExceededLabel,
    safetyLimitLabel,
  };
};
